/**
 * CTO Directive #120.1 §1 — Real audio-chain verification.
 *
 * This module owns the audio playback contract between ME and the
 * user's actual speaker. The rule is:
 *
 *     1. Ask the backend to speak — /api/me/voice/speak returns a
 *        base64 audio blob + mime type.
 *     2. Post ``requested`` immediately so the Living Conversation
 *        Stream carries the intent.
 *     3. Decode the blob into an ``<audio>`` element.
 *     4. Post ``ready`` the moment the element loaded metadata.
 *     5. Call ``.play()`` — post ``started`` on the ``playing``
 *        event, ``completed`` on ``ended``, ``failed`` on any
 *        error (including autoplay blocks).
 *     6. If — and only if — the backend returned no audio, fall
 *        back to Web Speech API and mark the playback as
 *        ``failed`` with ``error_kind = "browser_fallback"``.
 *
 * The UI reads the ``AudioChainState`` returned by this module to
 * decide what to say to the user. If playback is not verified,
 * ME must NOT claim it is speaking.
 */

import { api } from "@/lib/api";

// The five ``PlaybackEvent`` phases the backend accepts. Keep them
// in sync with app/modules/voice/router.py::_PLAYBACK_KIND_MAP.
export const PLAYBACK_PHASES = Object.freeze([
  "requested", "ready", "started", "completed", "failed",
]);

// Global unlock latch — some browsers refuse audio playback until
// the first user gesture. We attach a one-shot pointer listener that
// primes a shared AudioContext + a silent audio element.
let _audioUnlocked = false;
let _sharedAudio = null;

export function primeAudioOnFirstGesture() {
  if (typeof window === "undefined" || _audioUnlocked) return;
  const prime = () => {
    try {
      const el = document.createElement("audio");
      el.setAttribute("data-testid", "audio-unlock-latch");
      el.muted = true;
      el.src =
        "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgLsAAIC7AAABAAgAZGF0YQAAAAA=";
      el.play().catch(() => {});
      _sharedAudio = el;
      _audioUnlocked = true;
    } catch {
      /* silent */
    }
    window.removeEventListener("pointerdown", prime);
    window.removeEventListener("keydown", prime);
  };
  window.addEventListener("pointerdown", prime, { once: true });
  window.addEventListener("keydown", prime, { once: true });
}

async function postPhase(phase, extras = {}) {
  try {
    await api.post("/me/voice/playback", { phase, ...extras });
  } catch {
    // Silent — the audio chain must never fail because the
    // heartbeat POST hiccuped.
  }
}

function base64ToBlob(b64, mime) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime || "audio/mpeg" });
}

// CTO Directive #126.2 — the "dur" / "sus" voice intent stops any
// current playback. We track active handles module-scope so callers
// can cancel every ongoing spoken utterance in one call.
const _activeHandles = new Set();

// CTO — while ANY spoken audio is playing the microphone is gated so
// ME never transcribes its own voice. The living page registers the
// gate; playback acquires/releases it automatically.
let _micGate = null;
export function setMicGate(fn) { _micGate = fn; }
function _updateMicGate() {
  try { _micGate && _micGate(_activeHandles.size > 0); } catch { /* silent */ }
}

export function cancelAllSpokenAudio() {
  const list = Array.from(_activeHandles);
  _activeHandles.clear();
  _updateMicGate();
  for (const h of list) {
    try { h.cancel(); } catch { /* silent */ }
  }
  return list.length;
}

/**
 * The real audio playback loop. Returns an object with a promise
 * that resolves to the final ``AudioChainState``:
 *
 *     { verified: boolean, phase: "completed" | "failed",
 *       error_kind?: string, duration_ms?: number }
 */
export function playSpokenAudio({
  audioBase64,
  mimeType = "audio/mpeg",
  utteranceId,
  onPhase = () => {},
}) {
  const uttId = utteranceId || `utt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  function fire(phase, extras = {}) {
    onPhase({ phase, utterance_id: uttId, ...extras });
    postPhase(phase, { utterance_id: uttId, ...extras });
  }

  fire("requested", {
    audio_bytes: audioBase64 ? Math.floor((audioBase64.length * 3) / 4) : 0,
  });

  if (!audioBase64) {
    fire("failed", { error_kind: "no_audio_from_backend" });
    return {
      done: Promise.resolve({
        verified: false, phase: "failed",
        error_kind: "no_audio_from_backend",
      }),
      cancel: () => {},
    };
  }

  if (typeof window === "undefined" || typeof Audio === "undefined") {
    fire("failed", { error_kind: "no_audio_element" });
    return {
      done: Promise.resolve({
        verified: false, phase: "failed",
        error_kind: "no_audio_element",
      }),
      cancel: () => {},
    };
  }

  const blob = base64ToBlob(audioBase64, mimeType);
  const url = URL.createObjectURL(blob);
  const el = new Audio(url);
  el.setAttribute("data-testid", "me-audio-playback");
  const startedAt = performance.now();

  const done = new Promise((resolve) => {
    let finished = false;
    const finish = (state) => {
      if (finished) return;
      finished = true;
      try { URL.revokeObjectURL(url); } catch { /* silent */ }
      resolve(state);
    };
    el.addEventListener("loadedmetadata", () => {
      fire("ready", { duration_ms: (el.duration || 0) * 1000 });
    });
    el.addEventListener("playing", () => {
      fire("started");
    });
    el.addEventListener("ended", () => {
      const dur = performance.now() - startedAt;
      fire("completed", {
        duration_ms: dur,
        audio_bytes: blob.size,
      });
      finish({ verified: true, phase: "completed", duration_ms: dur });
    });
    el.addEventListener("error", () => {
      fire("failed", { error_kind: "media_error" });
      finish({ verified: false, phase: "failed", error_kind: "media_error" });
    });
    el.play().catch(() => {
      fire("failed", { error_kind: "autoplay_blocked" });
      finish({
        verified: false, phase: "failed",
        error_kind: "autoplay_blocked",
      });
    });
  });

  const handle = {
    done,
    cancel: () => {
      try { el.pause(); } catch { /* silent */ }
      try { URL.revokeObjectURL(url); } catch { /* silent */ }
      _activeHandles.delete(handle);
      _updateMicGate();
    },
  };
  _activeHandles.add(handle);
  _updateMicGate();
  done.finally(() => { _activeHandles.delete(handle); _updateMicGate(); });
  return handle;
}

/**
 * A tiny helper the LivingOrb can use to render a one-line audio
 * status caption. Returns the Turkish string per CTO §1.
 */
export function audioStateCaptionTr(state) {
  switch (state) {
    case "requested": return "Ses hazırlanıyor";
    case "ready":     return "Ses hazır";
    case "started":   return "Konuşuyor";
    case "completed": return "";
    case "failed":    return "Ses çıkışı bekleniyor";
    case "blocked":   return "Ses izni gerekli";
    default:          return "";
  }
}

/**
 * The honest failure sentence. If playback did not verify, this is
 * what ME says out loud — no "sistem çalışıyor" lie.
 */
export function honestFailureSentenceTr() {
  return (
    "Yanıtımı hazırladım ancak ses çıkışını doğrulayamadım. "
    + "Ses aygıtını birlikte kontrol edelim."
  );
}
